/**
 * Deterministic training availability.
 *
 * Real bookings need a backend; for this demo we generate a stable, realistic
 * pattern of open dates from a seed (a location slug, or "team" for the generic
 * team page) so the calendar is consistent across renders, never needs a
 * database, and differs sensibly per location:
 *
 *   - Online sessions run on roughly half of all weekdays.
 *   - In-person full days are scarcer: two or three weekdays a month, and the
 *     specific days depend on the location (its tutor's travel schedule).
 *
 * Weekends are never offered (full-day weekday training). A day is either
 * in-person, online, or unavailable, never both (a tutor on-site that day is
 * not also taking online sessions).
 */

export type Availability = "online" | "inperson";

/** FNV-1a — small, fast, stable string hash (no Math.random / Date.now). */
export function hashStr(input: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Availability for every weekday of a month, keyed by day-of-month (1-based).
 * Pure: same (seed, year, month) always yields the same map.
 */
export function monthAvailability(
  seed: string,
  year: number,
  month: number,
): Map<number, Availability> {
  const result = new Map<number, Availability>();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weekdays: number[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow >= 1 && dow <= 5) weekdays.push(d);
  }

  // Two or three in-person full days a month, chosen deterministically.
  const inPersonCount = 2 + (hashStr(`${seed}|${year}|${month}|count`) % 2);
  const ranked = [...weekdays].sort(
    (a, b) =>
      hashStr(`${seed}|${year}|${month}|${a}|ip`) -
      hashStr(`${seed}|${year}|${month}|${b}|ip`),
  );
  const inPerson = new Set(ranked.slice(0, inPersonCount));

  for (const d of weekdays) {
    if (inPerson.has(d)) {
      result.set(d, "inperson");
    } else if (hashStr(`${seed}|${year}|${month}|${d}|on`) % 2 === 0) {
      // Roughly half of the remaining weekdays carry an online session.
      result.set(d, "online");
    }
  }

  return result;
}

const FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** "Wednesday, 15 July 2026" for a local Date. */
export function formatLongDate(date: Date): string {
  return FMT.format(date);
}

export const FORMAT_LABEL: Record<Availability, string> = {
  online: "Online session",
  inperson: "In-person full day",
};
